"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useRef, type MutableRefObject } from "react";
import { useSnapshot } from "valtio";

import { EdgenModalCreateAble } from "@components/EdgenModal";
import { openOnboardingWelcomeModal } from "@components/OnboardingWelcomeModal/openOnboardingWelcomeModal";
import { tryOpenTrackerReportAutoshow } from "@features/tracker/trackerReportAutoshowStorage";
import { useBridge } from "@/src/lib/bridge";
import { rootStore } from "@store/rootStore";
import { ModalStore } from "@store/modules/modal";
import { useGlobalAppModalLimit } from "@/src/hooks/useGlobalAppModalLimit";
import { useTrackerTabUnreadDot } from "./useTrackerTabUnreadDot";
import { usePathname } from "@/src/hooks/usePathname";
import { isInterruptiveSurfaceRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import type { OpenOnboardingWelcomeModalParams } from "@components/OnboardingWelcomeModal/types";

const ONBOARDING_WELCOME_REPORT_ID = "onboarding-welcome";

function isPeerAutoModalOpen(): boolean {
  return !!(
    ModalStore.appVersionUpdateModalVisible ||
    ModalStore.turnOnNotificationModalVisible ||
    ModalStore.lowCreditModalVisible ||
    ModalStore.appRatingModalVisible
  );
}

async function showTrackerWelcomeModal(
  params: OpenOnboardingWelcomeModalParams,
  isOpenRef: MutableRefObject<boolean>,
) {
  if (isPeerAutoModalOpen()) {
    return;
  }

  isOpenRef.current = true;
  ModalStore.trackerWelcomeModalVisible = true;
  try {
    await openOnboardingWelcomeModal(params);
  } finally {
    isOpenRef.current = false;
    ModalStore.trackerWelcomeModalVisible = false;
  }
}

/**
 * Auto-opens onboarding welcome when a logged-in user has multiple unread trackers.
 * Lives at app root (RootLayoutClient) so PC and H5 share the same 24h gate and
 * single mount attempt per session.
 *
 * App 多 WebView：`useGlobalAppModalLimit` 协调 visible / broadcast / 远程关弹。
 * Web/H5 单页：保持原逻辑（不校验 visible、claim 前即标记 attempted）。
 */
export function useTrackerWelcomeAutoshow() {
  const { isInApp } = useBridge();
  const pathname = usePathname(true);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const {
    appVersionUpdateModalVisible,
    turnOnNotificationModalVisible,
    lowCreditModalVisible,
    appRatingModalVisible,
    trackerWelcomeModalVisible,
  } = useSnapshot(ModalStore);
  const userName = ownerInfo?.name;
  const ownerId = ownerInfo?.id;
  const { unreadTrackers } = useTrackerTabUnreadDot();
  const attemptedRef = useRef(false);
  const isWelcomeOpenRef = useRef(false);
  const { canShow, isSuppressed, tryOpen } = useGlobalAppModalLimit(
    ONBOARDING_WELCOME_REPORT_ID,
    {
      hideInAppChatModal: true,
      onRemoteClaim: () => {
        attemptedRef.current = true;
        isWelcomeOpenRef.current = false;
        ModalStore.trackerWelcomeModalVisible = false;
        EdgenModalCreateAble.end(false);
      },
    },
  );
  const tryOpenRef = useRef(tryOpen);
  tryOpenRef.current = tryOpen;

  useEffect(() => {
    if (!trackerWelcomeModalVisible && isWelcomeOpenRef.current) {
      isWelcomeOpenRef.current = false;
      EdgenModalCreateAble.end(false);
    }
  }, [trackerWelcomeModalVisible]);

  useEffect(() => {
    if (!isLoggedIn || unreadTrackers.length <= 1) {
      return;
    }
    if (isInterruptiveSurfaceRoute(pathname)) {
      return;
    }
    if (attemptedRef.current) {
      return;
    }
    if (isPeerAutoModalOpen()) {
      return;
    }

    Track.view("catchup_trigger_eligible", {
      unread_count: String(unreadTrackers.length),
    });

    const welcomeParams: OpenOnboardingWelcomeModalParams = {
      userName: userName ?? "",
      itemCount: unreadTrackers.length,
    };

    const openWelcome = () => {
      void showTrackerWelcomeModal(welcomeParams, isWelcomeOpenRef);
    };

    if (!isInApp) {
      attemptedRef.current = true;
      tryOpenTrackerReportAutoshow({
        ownerId,
        reportId: ONBOARDING_WELCOME_REPORT_ID,
        logTag: "tracker-welcome-autoshow",
        open: openWelcome,
      });
      return;
    }

    if (isSuppressed) {
      attemptedRef.current = true;
      return;
    }
    if (!canShow) {
      return;
    }

    attemptedRef.current = true;
    tryOpenTrackerReportAutoshow({
      ownerId,
      reportId: ONBOARDING_WELCOME_REPORT_ID,
      logTag: "tracker-welcome-autoshow",
      open: () => {
        tryOpenRef.current(openWelcome);
      },
    });
  }, [
    unreadTrackers.length,
    userName,
    isLoggedIn,
    ownerId,
    isInApp,
    canShow,
    isSuppressed,
    pathname,
    appVersionUpdateModalVisible,
    turnOnNotificationModalVisible,
    lowCreditModalVisible,
    appRatingModalVisible,
  ]);
}
